import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Button as CrcButton } from '@crc-evans/ui-components';
import './GenerateMobilistModal.css';

// ── Helpers ──────────────────────────────────────────
const required = (value) => !value && value !== 0;

// ── Steel Cutbacks ───────────────────────────────────
// Two boxes per pipeline: the cutback value in the first box, and
// the whole "+/-10" tolerance typed into a single second box, e.g.
// value "350/175" + tolerance "+/-10" → "350/175 +/-10".
const createSteelCutback = () => ({ value: '', tolerance: '' });

// Tolerance text from an earlier split + / - object shape.
const toleranceFromSides = (entry) => {
  const plus = String(entry.tolerancePlus || '').trim();
  const minus = String(entry.toleranceMinus || '').trim();
  if (plus && minus) return `+${plus}/-${minus}`;
  if (plus) return `+${plus}`;
  if (minus) return `-${minus}`;
  if (entry.tolerance) {
    const sign = entry.toleranceSign;
    return sign === '+' || sign === '-' ? `${sign}${entry.tolerance}` : `+/-${entry.tolerance}`;
  }
  return '';
};

// Splits a plain string like "350/175 +/-10" or "350 (+10 / -5)"
// into the value box and the single tolerance box.
const parseSteelCutbackString = (str) => {
  const s = String(str).trim();
  let match = s.match(/^(.*?)\s*\(\s*\+(\d+(?:\.\d+)?)\s*\/\s*-(\d+(?:\.\d+)?)\s*\)$/);
  if (match) return { value: match[1].trim(), tolerance: `+${match[2]}/-${match[3]}` };
  match = s.match(/^(.*?)\s*(\+\/-|±|\+|-)\s*(\d+(?:\.\d+)?)$/);
  if (match && match[1].trim()) {
    const sign = match[2] === '±' ? '+/-' : match[2];
    return { value: match[1].trim(), tolerance: `${sign}${match[3]}` };
  }
  return { value: s, tolerance: '' };
};

// Object-shaped entry → {value, tolerance} for the two boxes.
const toSteelCutbackEntry = (entry) => {
  if (entry === null || entry === undefined) return createSteelCutback();
  if (typeof entry !== 'object') return parseSteelCutbackString(entry);
  if ('value' in entry) {
    return { value: entry.value || '', tolerance: entry.tolerance ?? toleranceFromSides(entry) };
  }
  return {
    value: entry.secondary ? `${entry.primary}/${entry.secondary}` : `${entry.primary || ''}`,
    tolerance: toleranceFromSides(entry),
  };
};

// Formatted display/submission string for one cutback entry.
const formatSteelCutback = (entry) => {
  if (!entry || !String(entry.value || '').trim()) return '';
  const value = String(entry.value).trim();
  const tolerance = String(entry.tolerance || '').trim().replace(/^±/, '+/-');
  if (!tolerance) return value;
  return /^(\+\/-|\+|-)/.test(tolerance) ? `${value} ${tolerance}` : `${value} +/-${tolerance}`;
};

// Normalises to a single-entry array of {value, tolerance}.
const normalizeSteelCutbacks = (value) => {
  if (Array.isArray(value) && value.length > 0) {
    return value.map(toSteelCutbackEntry).slice(0, 1); // single cutback entry per pipeline
  }
  if (value === undefined || value === null || value === '') return [createSteelCutback()];
  return [toSteelCutbackEntry(value)];
};

const useForm = (initialValues = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});

  const setValue = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const setNestedValue = (section, index, field, value) => {
    setValues((prev) => {
      const arr = prev[section] ? [...prev[section]] : [];
      arr[index] = { ...(arr[index] || {}), [field]: value };
      return { ...prev, [section]: arr };
    });
    setErrors((prev) => ({ ...prev, [`${section}.${index}.${field}`]: '' }));
  };

  const reset = (defaults = {}) => {
    setValues(defaults);
    setErrors({});
  };

  const validate = (numPipelines) => {
    const errs = {};
    if (required(values.projectName))   errs.projectName   = 'Please enter project name';
    if (required(values.projectNumber)) errs.projectNumber = 'Please enter project number';
    if (required(values.createdBy))     errs.createdBy     = 'Please enter creator name';
    if (required(values.numberOfPipelines)) errs.numberOfPipelines = 'Please enter number of pipelines';

    for (let i = 0; i < numPipelines; i++) {
      const p = (values.pipelines || [])[i] || {};
      if (required(p.pipeSize))             errs[`pipelines.${i}.pipeSize`]             = 'Required';
      if (required(p.wallThickness))        errs[`pipelines.${i}.wallThickness`]        = 'Required';
      if (required(p.numberOfFieldJoints))  errs[`pipelines.${i}.numberOfFieldJoints`]  = 'Required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  return { values, errors, setValue, setNestedValue, reset, validate };
};

// ── Field components ──────────────────────────────────
const Field = ({ label, required: req, error, children }) => (
  <div className="gm-field">
    <label className={`gm-label${req ? ' gm-label-required' : ''}`}>{label}</label>
    {children}
    {error && <span className="gm-error">{error}</span>}
  </div>
);

// ── Main component ─────────────────────────────────────
const GenerateMobilistModal = ({
  visible,
  onCancel,
  onSubmit,
  initialValues = null,
  title = 'Generate New Mobilist',
  submitLabel = 'Generate',
}) => {
  const DEFAULTS = { projectType: 'Onshore', shifts: 'Single', numberOfPipelines: 1 };
  const { values, errors, setValue, setNestedValue, reset, validate } = useForm(DEFAULTS);

  // Pre-fill from an existing mobilist when editing; blank defaults otherwise.
  const getInitialValues = () => {
    if (!initialValues) return DEFAULTS;
    return {
      ...DEFAULTS,
      projectName: initialValues.projectName || '',
      projectNumber: initialValues.projectNumber || '',
      wbsElement: initialValues.wbsElement || '',
      projectType: initialValues.projectType || DEFAULTS.projectType,
      shifts: initialValues.shifts || DEFAULTS.shifts,
      productionDays: initialValues.productionDays || '',
      numberOfStations: initialValues.numberOfStations || '',
      totalPersonnel: initialValues.totalPersonnel || '',
      createdBy: initialValues.createdBy || '',
      numberOfPipelines: initialValues.numberOfPipelines || DEFAULTS.numberOfPipelines,
      pipelines: (initialValues.pipelines || []).map((p) => ({
        ...p,
        steelCutbacks: Array.isArray(p.steelCutbacks) ? [...p.steelCutbacks] : p.steelCutbacks,
      })),
    };
  };

  useEffect(() => {
    if (visible) reset(getInitialValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const numPipelines = parseInt(values.numberOfPipelines, 10) || 1;

  // ── Steel Cutback handlers (per pipeline) ──────────
  const updateSteelCutback = (pipeIndex, cutbackIndex, field, value, current) => {
    const list = normalizeSteelCutbacks(current).map((entry, idx) =>
      idx === cutbackIndex ? { ...entry, [field]: value } : entry,
    );
    setNestedValue('pipelines', pipeIndex, 'steelCutbacks', list);
  };

  const handleSubmit = () => {
    if (validate(numPipelines)) {
      // Normalise steel cutbacks: combine the value box and the
      // single "+/-10" box into a ready-to-display string
      // (e.g. "350/175 +/-10") for the detail view / reports.
      const submitted = {
        ...values,
        pipelines: Array.from({ length: numPipelines }, (_, i) => {
          const p = (values.pipelines || [])[i] || {};
          const steelCutbacks = normalizeSteelCutbacks(p.steelCutbacks)
            .map(formatSteelCutback)
            .filter(Boolean);
          return {
            ...p,
            steelCutbacks,
            steelCutbacksText: steelCutbacks.join(', '),
          };
        }),
      };
      onSubmit(submitted);
      reset(DEFAULTS);
    }
  };

  const handleCancel = () => {
    reset(DEFAULTS);
    onCancel();
  };

  // Close on overlay click
  const overlayRef = useRef(null);
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) handleCancel();
  };

  if (!visible) return null;

  const modal = (
    <div className="gm-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="gm-box">

        {/* Header */}
        <div className="gm-header">
          <h2 className="gm-title">{title}</h2>
          <button className="gm-close" onClick={handleCancel} aria-label="Close">✕</button>
        </div>

        {/* Body */}
        <div className="gm-body">

          {/* General Information */}
          <p className="gm-section-heading">General Information</p>

          <div className="gm-row gm-row-3">
            <Field label="Project Name" required error={errors.projectName}>
              <input
                className="gm-input"
                placeholder="Enter Project Name"
                value={values.projectName || ''}
                onChange={(e) => setValue('projectName', e.target.value)}
              />
            </Field>
            <Field label="Project Number" required error={errors.projectNumber}>
              <input
                className="gm-input"
                placeholder="Enter Project Number"
                value={values.projectNumber || ''}
                onChange={(e) => setValue('projectNumber', e.target.value)}
              />
            </Field>
            <Field label="WBS Element">
              <input
                className="gm-input"
                placeholder="e.g., TBC"
                value={values.wbsElement || ''}
                onChange={(e) => setValue('wbsElement', e.target.value)}
              />
            </Field>
          </div>

          <div className="gm-row gm-row-2">
            <Field label="Project Type" required error={errors.projectType}>
              <div className="gm-radio-group">
                {['Onshore', 'Offshore', 'Spoolbase', 'Landline'].map((opt) => (
                  <label key={opt} className="gm-radio-label">
                    <input
                      type="radio"
                      name="projectType"
                      value={opt}
                      checked={values.projectType === opt}
                      onChange={() => setValue('projectType', opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Shifts" required error={errors.shifts}>
              <div className="gm-radio-group">
                {['Single', 'Double'].map((opt) => (
                  <label key={opt} className="gm-radio-label">
                    <input
                      type="radio"
                      name="shifts"
                      value={opt}
                      checked={values.shifts === opt}
                      onChange={() => setValue('shifts', opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </Field>
          </div>

          <div className="gm-row gm-row-3">
            <Field label="Production Days">
              <input
                className="gm-input gm-input-number"
                type="number"
                min={1}
                placeholder="Enter days"
                value={values.productionDays || ''}
                onChange={(e) => setValue('productionDays', e.target.value)}
              />
            </Field>
            <Field label="No. of Stations">
              <input
                className="gm-input gm-input-number"
                type="number"
                min={1}
                placeholder="Enter stations"
                value={values.numberOfStations || ''}
                onChange={(e) => setValue('numberOfStations', e.target.value)}
              />
            </Field>
            <Field label="Total No. of CRCE Personnel">
              <input
                className="gm-input gm-input-number"
                type="number"
                min={1}
                placeholder="Enter personnel count"
                value={values.totalPersonnel || ''}
                onChange={(e) => setValue('totalPersonnel', e.target.value)}
              />
            </Field>
          </div>

          <div className="gm-row gm-row-2">
            <Field label="Created By" required error={errors.createdBy}>
              <input
                className="gm-input"
                placeholder="Enter Name"
                value={values.createdBy || ''}
                onChange={(e) => setValue('createdBy', e.target.value)}
              />
            </Field>
            <Field label="Number of Pipelines" required error={errors.numberOfPipelines}>
              <input
                className="gm-input gm-input-number"
                type="number"
                min={1}
                max={20}
                placeholder="e.g. 1"
                value={values.numberOfPipelines || ''}
                onChange={(e) => setValue('numberOfPipelines', e.target.value)}
              />
            </Field>
          </div>

          <hr className="gm-divider" />

          {/* Pipeline Details */}
          <p className="gm-section-heading">Pipeline Details</p>

          {Array.from({ length: numPipelines }, (_, i) => {
            const p = (values.pipelines || [])[i] || {};
            return (
              <div key={i} className="gm-pipeline-card">
                <p className="gm-pipeline-title">Pipeline {i + 1} Details</p>

                <div className="gm-row gm-row-3">
                  <Field label="Pipe Size" required error={errors[`pipelines.${i}.pipeSize`]}>
                    <input
                      className="gm-input"
                      placeholder="Enter size"
                      value={p.pipeSize || ''}
                      onChange={(e) => setNestedValue('pipelines', i, 'pipeSize', e.target.value)}
                    />
                  </Field>
                  <Field label="Wall Thickness (mm)" required error={errors[`pipelines.${i}.wallThickness`]}>
                    <input
                      className="gm-input gm-input-number"
                      type="number"
                      placeholder="e.g. 12"
                      value={p.wallThickness || ''}
                      onChange={(e) => setNestedValue('pipelines', i, 'wallThickness', e.target.value)}
                    />
                  </Field>
                  <Field label="No. of Field Joints" required error={errors[`pipelines.${i}.numberOfFieldJoints`]}>
                    <input
                      className="gm-input gm-input-number"
                      type="number"
                      placeholder="Enter count"
                      value={p.numberOfFieldJoints || ''}
                      onChange={(e) => setNestedValue('pipelines', i, 'numberOfFieldJoints', e.target.value)}
                    />
                  </Field>
                </div>

                <div className="gm-row gm-row-2">
                  <Field label="Factory Coating Type">
                    <input
                      className="gm-input"
                      placeholder="Enter type"
                      value={p.factoryCoatingType || ''}
                      onChange={(e) => setNestedValue('pipelines', i, 'factoryCoatingType', e.target.value)}
                    />
                  </Field>
                  <Field label="FJC Type">
                    <input
                      className="gm-input"
                      placeholder="Enter FJC type"
                      value={p.fjcType || ''}
                      onChange={(e) => setNestedValue('pipelines', i, 'fjcType', e.target.value)}
                    />
                  </Field>
                </div>

                {/* Steel Cutbacks — single entry per pipeline */}
                {(() => {
                  const steelCutbacks = normalizeSteelCutbacks(p.steelCutbacks);
                  return (
                    <div className="gm-steel-section">
                      <div className="gm-steel-header">
                        <label className="gm-label gm-steel-label">Steel Cutbacks (mm)</label>
                      </div>

                      {steelCutbacks.map((cutback, cbIdx) => (
                        <div key={cbIdx} className="gm-steel-row">
                          <input
                            className="gm-input gm-steel-value-input"
                            type="text"
                            placeholder="e.g. 350/175"
                            aria-label="Steel cutback"
                            value={cutback.value}
                            onChange={(e) => updateSteelCutback(i, cbIdx, 'value', e.target.value, p.steelCutbacks)}
                          />
                          <input
                            className="gm-input gm-steel-tolerance-input"
                            type="text"
                            placeholder="e.g. +/-10"
                            aria-label="Steel cutback tolerance"
                            value={cutback.tolerance}
                            onChange={(e) => updateSteelCutback(i, cbIdx, 'tolerance', e.target.value, p.steelCutbacks)}
                          />
                        </div>
                      ))}
                    </div>
                  );
                })()}

                <div className="gm-row gm-row-3">
                  <Field label="Infill Type">
                    <input
                      className="gm-input"
                      placeholder="Enter infill type"
                      value={p.infillType || ''}
                      onChange={(e) => setNestedValue('pipelines', i, 'infillType', e.target.value)}
                    />
                  </Field>
                  <Field label="Infill Cutbacks (mm)">
                    <input
                      className="gm-input gm-input-number"
                      type="number"
                      placeholder="e.g. 40"
                      value={p.infillCutbacks || ''}
                      onChange={(e) => setNestedValue('pipelines', i, 'infillCutbacks', e.target.value)}
                    />
                  </Field>
                  <Field label="Infill Thickness (mm)">
                    <input
                      className="gm-input gm-input-number"
                      type="number"
                      placeholder="e.g. 5"
                      value={p.infillThickness || ''}
                      onChange={(e) => setNestedValue('pipelines', i, 'infillThickness', e.target.value)}
                    />
                  </Field>
                </div>
              </div>
            );
          })}

        </div>

        {/* Footer */}
        <div className="gm-footer">
          <CrcButton label="Cancel" variant="secondary" onClick={handleCancel} size="small" />
          <CrcButton label={submitLabel} variant="primary" onClick={handleSubmit} size="small" />
        </div>

      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default GenerateMobilistModal;
